/**
 * 文本格式化指令：根据修饰符把绑定值渲染为价格 / 涨跌幅文本，并按需要追加涨跌色 class。
 *
 * 修饰符：
 *   - `.price` → 用 {@link formatStockPrice} 渲染（保留两位小数）
 *   - `.pct`   → 用 {@link formatStockChangePct} 渲染（'+1.23%' / '-1.23%' / '0%' / '--'）
 *   - `.onlyColor` → 仅同步涨跌色 class，不覆盖元素文本（需配合 `.price` / `.pct` 或 `{ changePct }`）
 *
 * 绑定值：
 *   - 基本类型：直接作为待格式化的值；`.pct` 会按值自动上色，`.price` 不上色（无 changePct 信息）。
 *   - 对象 `{ value, changePct }`：`value` 为待格式化值，`changePct` 为色 class 依据，
 *     适用于「价格列也要按涨跌幅上色」「pct 列展示与上色取自不同字段」等场景。
 *
 * @example
 *   <span v-format.price="item.lastPrice"></span>
 *   <span v-format.price="{ value: item.lastPrice, changePct: item.changePct }"></span>
 *   <span v-format.pct="item.changePct"></span>
 *   <span v-format.pct.onlyColor="{ value: item.changePct, changePct: item.changePct }">{{ unit }}</span>
 *
 * @module common/directive/format
 */

import { isNil, isPlainObject } from 'lodash-es';
import { clearStockColorClass, formatStockChangePct, formatStockPrice, syncStockColorClass } from '@/utils/biz/stock';

/** 已注册到元素的色 class 缓存键，避免重复 add/remove 引发多余 DOM 操作 */
const COLOR_CLASS_KEY = '_vfColorClass';

/**
 * 解析 binding.value：兼容 `{ value, changePct }` 对象与基本类型。
 * @param {{ value: unknown }} binding Vue 指令 binding
 * @returns {{ value: unknown, changePct: unknown }}
 */
function parseBindingValue(binding) {
  const raw = binding.value;
  // 对象形态：value 与 changePct 拆分；缺省 changePct 视为 null（后续由模式决定是否退化）
  if (isPlainObject(raw)) {
    return {
      value: raw.value,
      changePct: isNil(raw.changePct) ? null : raw.changePct
    };
  }
  return { value: raw, changePct: null };
}

/**
 * 根据修饰符选择格式化器并渲染字符串。
 * 未识别的修饰符组合：直接转字符串透传，避免吞掉业务原文。
 * @param {{ value: unknown }} binding Vue 指令 binding
 * @returns {string}
 */
function renderText(binding) {
  const { value } = parseBindingValue(binding);
  const modifiers = binding.modifiers || {};
  if (modifiers.price) {
    return formatStockPrice(value);
  }
  if (modifiers.pct) {
    return formatStockChangePct(value);
  }
  // 兜底：未识别格式化模式，转为字符串直出
  return isNil(value) ? '' : String(value);
}

/**
 * 选择上色依据：
 *   - 显式 `changePct` 优先（适用于价格列按涨跌幅上色等场景）
 *   - 否则 `.pct` 模式下退回为待格式化值自身
 *   - 其它情况（如 `.price` 未传 changePct）不参与上色
 * @param {{ value: unknown, modifiers?: Record<string, boolean> }} binding Vue 指令 binding
 * @returns {unknown}
 */
function resolveColorBasis(binding) {
  const modifiers = binding.modifiers || {};
  const { value, changePct } = parseBindingValue(binding);
  if (!isNil(changePct)) {
    return changePct;
  }
  if (modifiers.pct) {
    // .pct 模式下值本身就是涨跌幅，可直接上色
    return value;
  }
  return null;
}

/**
 * 把渲染文本写入元素，并同步色 class。
 * @param {HTMLElement} el 指令所绑定的元素
 * @param {object} binding Vue 指令 binding
 * @returns {void}
 */
function applyBinding(el, binding) {
  const modifiers = binding.modifiers || {};
  // onlyColor：保留模板原文，仅按 pct / changePct 同步涨跌色
  if (!modifiers.onlyColor) {
    el.textContent = renderText(binding);
  }
  // basis 为 nil 时不写入默认灰：静态文字（如表头）不应被强行染色
  syncStockColorClass(el, resolveColorBasis(binding), COLOR_CLASS_KEY);
}

export default {
  mounted(el, binding) {
    applyBinding(el, binding);
  },
  updated(el, binding) {
    applyBinding(el, binding);
  },
  unmounted(el) {
    // 卸载时移除色 class，避免父级 keep-alive 复用导致旧 class 残留
    clearStockColorClass(el, COLOR_CLASS_KEY);
  }
};
